import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SearchInput from "@/components/SearchInput";
import { fetchAIRecommendations, aiResultToDecisions, type AIErrorType } from "@/lib/aiDecisionEngine";
import { classifyQuery, getQueryBehavior } from "@/lib/queryTaxonomy";
import { parseQuery } from "@/lib/queryParser";
import LinkChecker from "@/components/LinkChecker";
import SessionContinuity from "@/components/SessionContinuity";
import TrustStrip from "@/components/TrustStrip";
import SuggestionChips from "@/components/SuggestionChips";
import ClarificationStep from "@/components/ClarificationStep";
import DecisionCard from "@/components/DecisionCard";
import DecisionSummaryBar from "@/components/DecisionSummaryBar";
import QuickCompare from "@/components/QuickCompare";
import PostResultLoop from "@/components/PostResultLoop";
import FeedbackLoop from "@/components/FeedbackLoop";
import DecisionReceipt from "@/components/DecisionReceipt";
import NoConfidenceState from "@/components/NoConfidenceState";
import MarketFallbackBanner from "@/components/MarketFallbackBanner";
import ShareDecision from "@/components/ShareDecision";
import ValueProofBar from "@/components/ValueProofBar";

import UpgradeModal from "@/components/UpgradeModal";

import DecisionExample from "@/components/DecisionExample";
import HowItWorks from "@/components/HowItWorks";
import SocialProofStrip from "@/components/SocialProofStrip";
import WedgeBadges from "@/components/WedgeBadges";
import WhyNotSearch from "@/components/WhyNotSearch";
import PricingSection from "@/components/PricingSection";
import SiteFooter from "@/components/SiteFooter";
import {
  runDecisionEngine,
  type DecisionOption,
  type ClarificationQuestion,
  type CoverageState,
  type TrustProof,
} from "@/lib/decisionEngine";
import type { ParsedQuery } from "@/lib/queryParser";
import { updateMemoryFromQuery, loadMemory, addSearchToMemory } from "@/lib/userMemory";
// Client-side usage counter is display-only — server enforces real limits
import { addToHistory, getHistoryEntries } from "@/lib/watchlistStore";
import { detectLocation, getCountryFlag, type GeoData } from "@/lib/geolocation";
import { useAuth } from "@/contexts/AuthContext";
import { saveDecisionToCloud, logEvent } from "@/lib/cloudServices";
import { validateQueryIntent, sanitizeVoiceTranscript } from "@/lib/queryIntentValidator";
import { MapPin } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import UsagePill from "@/components/UsagePill";
import RefineBar, { type RefineParams } from "@/components/RefineBar";
import Logo from "@/components/Logo";
import { trackSearch, trackDecisionShown, trackClarificationAnswer, trackHomepageSearchStarted, trackPostResultSecondSearch } from "@/lib/behaviorTracking";

type Phase = "idle" | "loading" | "clarifying" | "results" | "no-confidence" | "rejected";

// Long-load safety: show fallback message after this many seconds
const LONG_LOAD_THRESHOLD_MS = 15000;

const loadingMessages = [
  "Analyzing your query...",
  "Evaluating options for your market...",
  "Applying category-specific scoring...",
  "Evaluating trust and reliability...",
  "Ranking by quality, price, and delivery...",
  "Finalizing recommendation...",
];

export default function Index() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [query, setQuery] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [parsed, setParsed] = useState<ParsedQuery | null>(null);
  const [clarifications, setClarifications] = useState<ClarificationQuestion[]>([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [clarificationAnswers, setClarificationAnswers] = useState<{ question: string; answer: string }[]>([]);
  const [decisions, setDecisions] = useState<DecisionOption[]>([]);
  const [coverage, setCoverage] = useState<CoverageState>("demo");
  const [trustProof, setTrustProof] = useState<TrustProof | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [intentKey, setIntentKey] = useState<string | undefined>();
  const [marketSupported, setMarketSupported] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [longLoad, setLongLoad] = useState(false);
  const [isTrialResult, setIsTrialResult] = useState(false);
  const [trialExhaustedFlag, setTrialExhaustedFlag] = useState(false);

  // Auto-open upgrade modal when redirected from auth with upgrade intent
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "true") {
      window.history.replaceState({}, "", "/");
      setShowUpgrade(true);
    }
  }, []);
  
  const { user } = useAuth();
  const [geo, setGeo] = useState<GeoData | null>(null);

  const hasHistory = getHistoryEntries().length > 0;
  const mem = loadMemory();
  const isReturningUser = mem.queriesCount >= 1 || hasHistory;

  useEffect(() => { detectLocation().then(setGeo); }, []);

  // Handle ?q= param from History/Watchlist re-run
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (q) {
      window.history.replaceState({}, "", "/");
      triggerSearch(q);
    }
  }, []);

  useEffect(() => {
    if (phase !== "loading") { setLoadingMsg(0); setLongLoad(false); return; }
    const interval = setInterval(() => {
      setLoadingMsg((i) => Math.min(i + 1, loadingMessages.length - 1));
    }, 500);
    const longTimer = setTimeout(() => setLongLoad(true), LONG_LOAD_THRESHOLD_MS);
    return () => { clearInterval(interval); clearTimeout(longTimer); };
  }, [phase]);

  const handleResults = useCallback((result: ReturnType<typeof runDecisionEngine>, q: string) => {
    setCoverage(result.coverage);
    setTrustProof(result.trustProof);
    setParsed(result.parsed);
    updateMemoryFromQuery(result.parsed.country, result.parsed.region, result.parsed.budget?.currency, result.parsed.category, result.parsed.priorities[0], result.parsed.localPreference, result.parsed.useCase);
    if (result.decisions) {
      setDecisions(result.decisions);
      const winner = result.decisions[0];
      addToHistory({ query: q, category: result.parsed.category, country: result.parsed.country, winnerName: winner.productName, winnerPrice: winner.price, winnerLabel: winner.label, confidence: winner.confidence, coverage: result.coverage });

      if (user) {
        saveDecisionToCloud({
          userId: user.id,
          rawQuery: q,
          parsedQuery: result.parsed as unknown as Record<string, unknown>,
          queryType: result.parsed.category,
          category: result.parsed.category,
          market: result.parsed.country ?? "",
          currency: result.parsed.budget?.currency ?? "",
          priorityMode: result.parsed.priorities[0] ?? "",
          confidenceLevel: winner.confidence,
          recommendationType: winner.label,
          winnerProductName: winner.productName,
          winnerPrice: winner.price,
          winnerLabel: winner.label,
          resultData: { decisions: result.decisions } as unknown as Record<string, unknown>,
          auditData: { trustProof: result.trustProof, coverage: result.coverage } as unknown as Record<string, unknown>,
        });
        logEvent({ userId: user.id, eventType: "decision_completed", metadata: { query: q, category: result.parsed.category } });
      }

      trackDecisionShown(q, winner.productName, winner.confidence, user?.id);

      const allInsufficient = result.decisions.every((d) => d.confidence === "insufficient");
      setPhase(allInsufficient ? "no-confidence" : "results");
    }
  }, [user]);

  const handleAIError = useCallback((errorType: AIErrorType | undefined, errorMsg: string) => {
    switch (errorType) {
      case "limit_reached":
      case "rate_limit_anonymous":
        toast({
          title: "Daily limit reached",
          description: user
            ? "Resets tomorrow. Upgrade for deeper validation, full decision breakdown, and more decisions per month."
            : "Create a free account to save decisions and unlock 5 Pro-level searches.",
        });
        setShowUpgrade(true);
        setPhase("idle");
        return true;
      case "rate_limit":
        toast({ title: "Monthly limit reached", description: "Your plan limit has been reached. Upgrade for more decisions or wait for your monthly reset." });
        setShowUpgrade(true);
        setPhase("idle");
        return true;
      case "upgrade_required":
        toast({ title: "Upgrade required", description: "This feature requires a paid plan." });
        setPhase("idle");
        return true;
      case "service_unavailable":
        toast({ title: "Temporarily unavailable", description: "The service is temporarily unavailable. Try again in a moment." });
        return false;
      default:
        return false;
    }
  }, [user]);

  const runAISearch = useCallback(async (q: string, parsedQuery: ReturnType<typeof parseQuery>, clAnswers?: { question: string; answer: string }[], refineParams?: RefineParams) => {
    const { recommendations, error, errorType, usage, intentKey: ik, marketSupported: ms, isTrialResult: itr, trialExhausted: te } = await fetchAIRecommendations(parsedQuery, q, clAnswers, refineParams);
    if (ik) setIntentKey(ik);
    if (ms !== undefined) setMarketSupported(ms);
    
    // Track trial state from response
    setIsTrialResult(!!itr);
    setTrialExhaustedFlag(!!te);
    
    // Sync usage pill with backend state
    if (usage) {
      import("@/components/UsagePill").then(({ notifyUsageUpdate }) => {
        notifyUsageUpdate(usage);
      });
    }

    // Show trial-exhausted upgrade prompt
    if (te) {
      toast({
        title: "You've used all 5 Pro-level decisions",
        description: "See why products win, what you're giving up, and where to buy with confidence. Upgrade to keep that level.",
      });
      setShowUpgrade(true);
    }

    if (recommendations) {
      const { decisions: aiDecisions, trustProof: aiProof, queryBehavior } = aiResultToDecisions(
        recommendations,
        parsedQuery,
        "partial"
      );
      const syntheticResult = {
        parsed: parsedQuery,
        decisions: aiDecisions,
        coverage: "partial" as const,
        trustProof: aiProof,
      };
      handleResults(syntheticResult, q);
    } else {
      console.warn("AI fallback:", error);
      if (errorType && handleAIError(errorType, error || "")) return;
      setPhase("no-confidence");
    }
  }, [handleResults, handleAIError]);

  const triggerSearch = useCallback((q: string, sourceSurface: string = "search_input") => {
    // Voice transcript sanitization
    const sanitized = sanitizeVoiceTranscript(q).trim();
    if (!sanitized) return;

    // Intent validation gate
    const intentResult = validateQueryIntent(sanitized);
    console.log("[Intent]", { originalQuery: q, sanitized, ...intentResult });
    trackHomepageSearchStarted(undefined, !!user, sourceSurface);

    if (intentResult.intent === "rejected") {
      setQuery(sanitized);
      setRejectionReason(intentResult.reason);
      setPhase("rejected");
      return;
    }

    if (intentResult.intent === "ambiguous") {
      // Skip broken clarification flow. Proceed directly to AI search
      // which handles ambiguity better than empty-option clarification cards.
      setQuery(sanitized); setPhase("loading"); setDecisions([]); setClarifications([]); setAnsweredCount(0); setClarificationAnswers([]); setTrustProof(null); setParsed(null);
      addSearchToMemory(sanitized);
      trackSearch(sanitized, undefined, user?.id);
      const geoContext = geo ? { countryCode: geo.countryCode, city: geo.city, country: geo.country, currency: geo.currency } : undefined;
      const parsed = parseQuery(sanitized, geoContext);
      runAISearch(sanitized, parsed);
      return;
    }

    // Server enforces real limits — client just initiates the search
    setQuery(sanitized); setPhase("loading"); setDecisions([]); setClarifications([]); setAnsweredCount(0); setClarificationAnswers([]); setTrustProof(null); setParsed(null);
    addSearchToMemory(sanitized);
    trackSearch(sanitized, undefined, user?.id);

    const geoContext = geo ? { countryCode: geo.countryCode, city: geo.city, country: geo.country, currency: geo.currency } : undefined;
    const parsed = parseQuery(sanitized, geoContext);
    const localResult = runDecisionEngine(sanitized);

    const queryClass = classifyQuery(sanitized);
    const behavior = getQueryBehavior(queryClass);

    if (localResult.clarifications && behavior.maxClarifications > 0) {
      const limitedClarifications = localResult.clarifications.slice(0, behavior.maxClarifications);
      setCoverage(localResult.coverage); setTrustProof(localResult.trustProof); setParsed(localResult.parsed); setClarifications(limitedClarifications); setPhase("clarifying");
      return;
    }

    runAISearch(sanitized, parsed);
  }, [handleResults, runAISearch]);

  const handleClarificationAnswer = useCallback(
    (qId: string, answer: string) => {
      const next = answeredCount + 1;
      setAnsweredCount(next);
      
      // Store the answer
      const questionText = clarifications[answeredCount]?.question || qId;
      const newAnswers = [...clarificationAnswers, { question: questionText, answer }];
      setClarificationAnswers(newAnswers);
      trackClarificationAnswer(questionText, answer, user?.id);
      
      if (next >= clarifications.length) {
        setPhase("loading");
        const geoCtx = geo ? { countryCode: geo.countryCode, city: geo.city, country: geo.country, currency: geo.currency } : undefined;
        const parsed = parseQuery(query, geoCtx);
        runAISearch(query, parsed, newAnswers);
      }
    },
    [answeredCount, clarifications, clarificationAnswers, query, runAISearch]
  );

  const handleReset = () => { setPhase("idle"); setQuery(""); setDecisions([]); setClarifications([]); setAnsweredCount(0); setClarificationAnswers([]); setTrustProof(null); setParsed(null); };
  const handleRerun = () => { if (query) triggerSearch(query); };
  const handleRefine = useCallback((q: string, refineParams?: RefineParams) => {
    setPhase("loading"); setDecisions([]); setTrustProof(null); setParsed(null);
    const geoCtx = geo ? { countryCode: geo.countryCode, city: geo.city, country: geo.country, currency: geo.currency } : undefined;
    const parsedQ = parseQuery(q, geoCtx);
    runAISearch(q, parsedQ, undefined, refineParams);
  }, [geo, runAISearch]);
  const handleLinkCheck = (_url: string) => {};

  const winner = decisions[0];
  const alternatives = decisions.slice(1);

  return (
    <div className="min-h-screen bg-background pt-safe pb-[80px] md:pb-[72px] overflow-x-hidden">
      

      <header className="flex items-center justify-between px-5 py-3.5 max-w-6xl mx-auto">
        <button onClick={handleReset} aria-label="Decyzon home" className="text-foreground hover:opacity-80 transition-opacity">
          <Logo variant="wordmark" size="h-6" />
        </button>
        <div className="flex items-center gap-3">
          <UsagePill />
          {geo && (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-body">
              <MapPin className="w-3 h-3" />
              {getCountryFlag(geo.countryCode)} {geo.city ? `${geo.city}, ${geo.countryCode}` : geo.country}
            </span>
          )}
        </div>
      </header>

      <main className="px-5">
        <AnimatePresence mode="wait">
          {phase === "idle" && (
            <motion.div key="landing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>

              {/* ABOVE THE FOLD — minimal, tool-first */}
              <section className="flex flex-col items-center pt-12 sm:pt-20 pb-12 md:pb-16 px-1">
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center max-w-[560px] mx-auto">
                  <h1 className="text-[34px] sm:text-5xl md:text-6xl font-display font-bold text-foreground leading-[1.05] tracking-tight">
                    Find the best product.<br />
                    <span className="text-gradient-primary">Instantly.</span>
                  </h1>
                  <p className="text-muted-foreground font-body text-[14px] sm:text-base max-w-[420px] mx-auto leading-relaxed mt-4 sm:mt-5">
                    We compare price, delivery, and trust to give you the one that actually wins.
                  </p>
                </motion.div>

                {/* PRIMARY: search input — inline on all devices */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="w-full max-w-[560px] mx-auto mt-8 sm:mt-10"
                >
                  <SearchInput onSubmit={triggerSearch} isLoading={false} />
                </motion.div>

                {/* Confidence bullets */}
                <motion.ul
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 mt-5 max-w-[560px] mx-auto text-[12.5px] font-body text-muted-foreground/90"
                >
                  <li className="flex items-center gap-1.5"><span className="text-primary">✔</span> One clear recommendation</li>
                  <li className="flex items-center gap-1.5"><span className="text-primary">✔</span> Why it wins</li>
                  <li className="flex items-center gap-1.5"><span className="text-primary">✔</span> What you sacrifice</li>
                </motion.ul>

                {/* 3 fixed example queries */}
                <SuggestionChips onSearch={triggerSearch} />
              </section>

              {/* BELOW THE FOLD — strict order */}
              <div className="py-20">
                <DecisionExample />
              </div>

              {isReturningUser && (
                <div className="py-20">
                  <SessionContinuity onSearch={triggerSearch} onCheckLink={handleLinkCheck} />
                </div>
              )}

              <div className="py-20">
                <WhyNotSearch />
              </div>

              <div className="py-20">
                <HowItWorks />
              </div>

              <div className="py-20">
                <SocialProofStrip />
                <div className="mt-8">
                  <WedgeBadges />
                </div>
              </div>

              <div className="py-20">
                <PricingSection />
              </div>
            </motion.div>
          )}

          {phase === "loading" && (
            <motion.section key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} className="flex flex-col items-center justify-center pt-20 pb-[100px] md:pb-16">
              <div className="text-center mb-8">
                <p className="text-[11px] text-muted-foreground/90 font-body mb-2 uppercase tracking-wider">Analyzing</p>
                <h1 className="text-[22px] sm:text-3xl font-display font-bold text-foreground leading-tight tracking-tight">
                  {query}
                </h1>
              </div>
              <SearchInput onSubmit={triggerSearch} isLoading={true} />
              <motion.p key={loadingMsg} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-muted-foreground font-body mt-5">
                {loadingMessages[loadingMsg]}
              </motion.p>
              {longLoad ? (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-muted-foreground/70 font-body mt-3 text-center max-w-xs">
                  This is taking longer than usual. Still working on your decision.
                </motion.p>
              ) : (
                <p className="text-xs text-muted-foreground/50 font-body mt-2">Analyzing the market for your query.</p>
              )}
            </motion.section>
          )}

          {phase === "clarifying" && (
            <motion.section key="clarify" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center pt-20 pb-[100px] md:pb-16">
              <p className="text-sm text-muted-foreground font-body mb-6">
                Evaluating: <span className="text-foreground">{query}</span>
              </p>
              <ClarificationStep questions={clarifications} onAnswer={handleClarificationAnswer} answeredCount={answeredCount} />
            </motion.section>
          )}

          {phase === "rejected" && (
            <motion.section key="rejected" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center pt-20 pb-[100px] md:pb-16 max-w-lg mx-auto text-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <span className="text-xl">🔍</span>
              </div>
              <h2 className="text-lg font-display font-bold text-foreground mb-2">
                No product search detected.
              </h2>
              <p className="text-sm text-muted-foreground font-body mb-6">
                &ldquo;{query}&rdquo; doesn&apos;t look like a buying decision. Try asking for a specific product, budget, or use case.
              </p>
              <div className="space-y-2 mb-8 w-full">
                <p className="text-xs text-muted-foreground/70 font-body uppercase tracking-wider">Try something like</p>
                {["Best office chair under $300", "Noise cancelling headphones for travel", "Air fryer for a family of 4"].map((ex) => (
                  <button key={ex} onClick={() => triggerSearch(ex)} className="block w-full text-left px-4 py-2.5 rounded-lg border border-border bg-secondary text-secondary-foreground text-sm font-body hover:border-primary/40 hover:bg-primary/5 transition-all">
                    {ex}
                  </button>
                ))}
              </div>
              <SearchInput onSubmit={triggerSearch} isLoading={false} />
              <button onClick={handleReset} className="text-xs text-primary font-body hover:underline mt-4">Back to home</button>
            </motion.section>
          )}

          {phase === "no-confidence" && (
            <motion.section key="no-confidence" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto pt-12 pb-16">
              <div className="text-center mb-4">
                <button onClick={handleReset} className="text-sm text-primary font-body hover:underline mb-3 inline-block">New decision</button>
                <button onClick={() => triggerSearch(query)} className="text-sm text-muted-foreground font-body hover:text-foreground ml-4">Try again</button>
              </div>
              <NoConfidenceState query={query} onAction={(action) => {
                switch (action) {
                  case "retry": triggerSearch(query); break;
                  case "suggest": triggerSearch("best office chair under $300"); break;
                  case "expand": triggerSearch(query + " international"); break;
                  case "adjust": default: handleReset(); break;
                }
              }} />
            </motion.section>
          )}

          {phase === "results" && winner && (
            <motion.section key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto pt-6 pb-[100px] md:pb-16">
               <div className="text-center mb-5">
                <div className="flex items-center justify-center gap-4 mb-2">
                  <button onClick={handleReset} className="text-xs text-primary/80 font-body hover:underline">New decision</button>
                  <span className="text-border">·</span>
                  <button onClick={handleRerun} className="text-xs text-muted-foreground font-body hover:text-foreground hover:underline">Rerun this search</button>
                </div>
                <h2 className="text-lg sm:text-2xl font-display font-bold text-foreground">Your decision</h2>
                <p className="text-muted-foreground font-body text-sm mt-1">{query}</p>
                
                <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                  <ShareDecision query={query} winner={winner} />
                </div>
              </div>

              {/* Pro trial result banner */}
              {isTrialResult && (
                <div className="max-w-xl mx-auto mb-4 px-4 py-3 rounded-lg border border-primary/20 bg-primary/5 text-center">
                  <p className="text-xs font-body font-medium text-primary">
                    You're seeing a Pro-level decision.
                  </p>
                  <p className="text-[11px] font-body text-muted-foreground mt-0.5">
                    Deeper validation. Full audit. Better sources.
                  </p>
                </div>
              )}

              {parsed && trustProof && (
                <DecisionSummaryBar parsed={parsed} proof={trustProof} coverage={coverage} confidence={winner.confidence} />
              )}

              <RefineBar query={query} onRefine={handleRefine} />

              {trustProof && (
                <ValueProofBar proof={trustProof} coverage={coverage} winnerPrice={winner.price} />
              )}

              {/* Market fallback warning */}
              {!marketSupported && geo && (
                <MarketFallbackBanner marketCode={geo.countryCode} />
              )}

              {/* All-estimated banner */}
              {winner && alternatives.length > 0 && 
                winner.verification === "ai-estimated" && 
                alternatives.every(a => a.verification === "ai-estimated") && (
                <div className="max-w-xl mx-auto mb-3 px-3 py-2 rounded-lg border border-border/30 bg-muted/15 text-center">
                  <p className="text-[11px] text-muted-foreground font-body">
                    Limited live market data available. Scores are AI estimates based on current market knowledge.
                  </p>
                </div>
              )}

              <div className="max-w-xl mx-auto mb-5">
                <DecisionCard option={winner} index={0} coverage={coverage} query={query} isWinner />
              </div>

              {parsed && trustProof && (
                <div className="flex justify-center mb-5">
                  <DecisionReceipt query={query} parsed={parsed} winner={winner} proof={trustProof} coverage={coverage} />
                </div>
              )}

              {alternatives.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-muted-foreground font-body text-center mb-4 font-medium">Alternatives</p>
                  <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
                    {alternatives.map((d, i) => (
                      <DecisionCard key={d.id} option={d} index={i + 1} coverage={coverage} query={query} />
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-border/30 pt-8 mt-6">
                <FeedbackLoop query={query} intentKey={intentKey} market={parsed?.country} verification={decisions[0]?.verification} winnerLabel={decisions[0]?.label} />
              </div>
              <PostResultLoop onSelect={(followup) => { trackPostResultSecondSearch(parsed?.category, followup, !!user); triggerSearch(followup, "post_result_loop"); }} category={parsed?.category} />
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      <SiteFooter />

      {showUpgrade && (
        <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} trialExhausted={trialExhaustedFlag} />
      )}
    </div>
  );
}
